import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaClock, FaMap, FaMapMarked, FaMapMarkedAlt, FaRegClock, FaRoad, FaServer, FaArrowLeft, FaStar, FaUsers } from 'react-icons/fa';
import Fancybox from '../components/Fancybox';

const ConvoyItem = () => {
  const { id } = useParams();
  const [convoyItem, setConvoyItem] = useState(null);
  const [upcomingConvoys, setUpcomingConvoys] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const convoyRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (convoyRef.current) observer.observe(convoyRef.current);

    return () => observer.disconnect();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const formatter = new Intl.DateTimeFormat('ru', { year: 'numeric', month: '2-digit', day: '2-digit' });
    return formatter.format(date);
  };

  useEffect(() => {
    const convoyCacheKey = `convoyItem-${id}`;
    const convoyCacheTimestampKey = `convoyItemTimestamp-${id}`;
    const convoysCacheKey = `upcomingConvoys-${id}`;
    const convoysCacheTimestampKey = `upcomingConvoysTimestamp-${id}`;
  
    const isCacheValid = (cacheTimestampKey) => {
      const cachedTimestamp = localStorage.getItem(cacheTimestampKey);
      return cachedTimestamp && (new Date().getTime() - new Date(parseInt(cachedTimestamp)).getTime()) < 3 * 60 * 60 * 1000;
    };
  
    const fetchConvoyItem = async () => {
      try {
        if (localStorage.getItem(convoyCacheKey) && isCacheValid(convoyCacheTimestampKey)) {
          setConvoyItem(JSON.parse(localStorage.getItem(convoyCacheKey)));
        } else {
          const response = await fetch(`https://hub.mileageriot.com/api/${id}/event`);
          const data = await response.json();
          setConvoyItem(data);
          localStorage.setItem(convoyCacheKey, JSON.stringify(data));
          localStorage.setItem(convoyCacheTimestampKey, new Date().getTime().toString());
        }
      } catch (error) {
        console.error('Error fetching convoy item data:', error);
      }
    };
  
    const fetchUpcomingConvoys = async () => {
      try {
        if (localStorage.getItem(convoysCacheKey) && isCacheValid(convoysCacheTimestampKey)) {
          setUpcomingConvoys(JSON.parse(localStorage.getItem(convoysCacheKey)));
        } else {
          const response = await fetch(`https://hub.mileageriot.com/api/${id}/getUpcomingConvoys`);
          const data = await response.json();
          setUpcomingConvoys(data);
          localStorage.setItem(convoysCacheKey, JSON.stringify(data));
          localStorage.setItem(convoysCacheTimestampKey, new Date().getTime().toString());
        }
      } catch (error) {
        console.error('Error fetching upcoming convoys:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchConvoyItem();
    fetchUpcomingConvoys();
  }, [id]);

  if (loading) {
    return (
      <section className="convoy-item-modern page-convoy-item" ref={convoyRef}>
        <div className="container">
          <div className="text-center">
            <div className="loading-spinner">
              <div className="spinner-modern"></div>
            </div>
            <p className="mt-3 text-white-50">{t('events.loading') || 'Загружаем конвой...'}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="convoy-item-modern page-convoy-item" ref={convoyRef}>
      <div className="container">
        {/* Header */}
        <div className="convoy-item-header fade-in-up stagger-1">
          <Link to="/events" className="btn btn-back">
            <FaArrowLeft className="me-2" />
            {t('events.backToEvents') || 'Назад к конвоям'}
          </Link>
          
          <div className="section-badge mt-4">
            <FaStar className="me-2" />
            <span>{t('events.badge') || 'События'}</span>
          </div>
        </div>

        <div className="row fade-in-up stagger-2">
          {/* Main Content */}
          <div className="col-lg-8 mb-5">
            {convoyItem && (
              <div className="convoy-item-card glass-container">
                {/* Banner Image */}
                <div className="convoy-item-image-container">
                  <img 
                    src={convoyItem.banner || 'https://hub.mileageriot.com/assets/images/banner/banner.jpg'} 
                    className="convoy-item-image"
                    alt={convoyItem.title || 'Convoy Banner'}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
                    }}
                  />
                  <div className="convoy-item-overlay">
                    <div className="convoy-date-badge">
                      <FaCalendarAlt className="me-2" />
                      {formatDate(convoyItem.date)}
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="convoy-item-body">
                  <h1 className="convoy-item-title text-white">{convoyItem.title}</h1>
                  <div className="convoy-item-divider"></div>
                  
                  {/* Details Grid */}
                  <div className="convoy-details-grid">
                    <div className="convoy-detail-item">
                      <FaClock className="detail-icon text-primary" />
                      <div className="detail-content">
                        <span className="detail-label">{t('events.eventDetails.parkingTime')}:</span>
                        <span className="detail-value text-primary">{convoyItem.meetup_time} UTC</span>
                      </div>
                    </div>
                    
                    <div className="convoy-detail-item">
                      <FaRegClock className="detail-icon text-primary" />
                      <div className="detail-content">
                        <span className="detail-label">{t('events.eventDetails.departureTime')}:</span>
                        <span className="detail-value text-primary">{convoyItem.departure_time} UTC</span>
                      </div>
                    </div>
                    
                    <div className="convoy-detail-item">
                      <FaMapMarkedAlt className="detail-icon text-primary" />
                      <div className="detail-content">
                        <span className="detail-label">{t('events.eventDetails.startLocation')}:</span>
                        <span className="detail-value text-primary">{convoyItem.start_location}</span>
                      </div>
                    </div>
                    
                    <div className="convoy-detail-item">
                      <FaMapMarked className="detail-icon text-primary" />
                      <div className="detail-content">
                        <span className="detail-label">{t('events.eventDetails.endLocation')}:</span>
                        <span className="detail-value text-primary">{convoyItem.end_location}</span>
                      </div>
                    </div>
                    
                    <div className="convoy-detail-item">
                      <FaMap className="detail-icon text-primary" />
                      <div className="detail-content">
                        <span className="detail-label">{t('events.eventDetails.dlc')}:</span>
                        <span className="detail-value text-primary">{convoyItem.dlc}</span>
                      </div>
                    </div>
                    
                    <div className="convoy-detail-item">
                      <FaServer className="detail-icon text-primary" />
                      <div className="detail-content">
                        <span className="detail-label">{t('events.eventDetails.server')}:</span>
                        <span className="detail-value text-primary">{convoyItem.server}</span>
                      </div>
                    </div>
                    
                    <div className="convoy-detail-item">
                      <FaRoad className="detail-icon text-primary" />
                      <div className="detail-content">
                        <span className="detail-label">{t('events.eventDetails.distance')}:</span>
                        <span className="detail-value text-primary">{convoyItem.distance} KM</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Route Map */}
                  <div className="convoy-route-section">
                    <div className="convoy-item-divider"></div>
                    <h3 className="text-white mb-3">{t('events.routeMap') || 'Карта маршрута'}</h3>
                    <Fancybox
                      options={{
                        Carousel: {
                          infinite: false,
                        },
                      }}
                    >
                      <a data-fancybox="gallery" href={convoyItem.route || 'https://hub.mileageriot.com/assets/images/banner/banner.jpg'}>
                        <img 
                          src={convoyItem.route || 'https://hub.mileageriot.com/assets/images/banner/banner.jpg'} 
                          className="convoy-route-image"
                          alt={`${convoyItem.title} Route` || 'Route Map'}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
                          }}
                        />
                      </a>
                    </Fancybox>
                  </div>
                </div>
              </div> 
            )}
          </div>
          
          {/* Sidebar */}
          <div className="col-lg-4">
            <div className="convoy-sidebar">
              <h3 className="text-white mb-4">
                <FaUsers className="me-2 text-primary" />
                {t('events.upcomingConvoys') || 'Предстоящие конвои'}
              </h3>
              
              {upcomingConvoys.map((convoy, index) => (
                <div key={convoy.id} className="convoy-sidebar-card glass-container mb-4" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Link to={`/events/${convoy.id}`} className="sidebar-convoy-link">
                    <div className="sidebar-convoy-image-container">
                      <img 
                        className="sidebar-convoy-image" 
                        src={convoy.banner || 'https://hub.mileageriot.com/assets/images/banner/banner.jpg'} 
                        alt={convoy.title || 'Convoy Banner'}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
                        }}
                      />
                    </div>
                    <div className="sidebar-convoy-content">
                      <h5 className="text-white sidebar-convoy-title">{convoy.title}</h5>
                      <span className="text-primary sidebar-convoy-date">
                        <FaCalendarAlt className="me-1" />
                        {formatDate(convoy.date)}
                      </span>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="convoy-item-bg-decoration"></div>
    </section>
  );
};

export default ConvoyItem;